import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import { log } from "./log";

interface ErrorContext {
  error: { message: string; stack?: string; name?: string };
}

// --- Storefront remote logging ---

type StorefrontLogStatus = "info" | "error" | "warn";

interface StorefrontLogEntry {
  status: StorefrontLogStatus;
  message: string;
  metadata?: Record<string, unknown>;
}

let loggingEnabled: boolean | null = null;
let logBuffer: StorefrontLogEntry[] = [];
let sessionId: string | undefined;
let logEndpointUrl: string | undefined;

const FLUSH_INTERVAL_MS = 5000;
const SESSION_STORAGE_KEY = "redo-storefront-logging";
const MAX_LOG_BUFFER_SIZE = 25;

interface StorefrontLoggingSession {
  enabled: boolean;
  sessionId: string;
}

export function initStorefrontLogging({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}): void {
  try {
    const existing = getSessionStorageWithExpiry(SESSION_STORAGE_KEY) as
      | StorefrontLoggingSession
      | undefined;

    if (existing) {
      loggingEnabled = existing.enabled;
      sessionId = existing.sessionId;
    } else {
      const allowedPercentage =
        autocheck.storefrontLoggingAllowedPercentage ?? 1;
      loggingEnabled = Math.random() * 100 < allowedPercentage;
      sessionId = crypto.randomUUID();
      const session: StorefrontLoggingSession = {
        enabled: loggingEnabled,
        sessionId,
      };
      setSessionStorageWithExpiry(SESSION_STORAGE_KEY, session, 2);
    }

    if (!loggingEnabled) {
      return;
    }

    const apiUrl =
      globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
      process.env.REDO_SHOPIFY_SERVER_URL!;
    logEndpointUrl = `${apiUrl}/widgets/${autocheck.widgetSlug}/storefront-log`;

    setInterval(flushLogsBeacon, FLUSH_INTERVAL_MS);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushLogsBeacon();
      }
    });

    window.addEventListener("pagehide", flushLogsBeacon);
  } catch (e) {
    log("Failed to initialize storefront logging", { error: e });
  }
}

export function datadogLog(
  status: StorefrontLogStatus,
  message: string,
  metadata?: {},
): void {
  try {
    if (loggingEnabled === false) {
      return;
    }

    logBuffer.push({ status, message, metadata });
    if (logBuffer.length >= MAX_LOG_BUFFER_SIZE) {
      log("Log buffer is too full, dropping some logs");
    }
    logBuffer = logBuffer.slice(0, MAX_LOG_BUFFER_SIZE);
  } catch (e) {
    log("datadogLog failed", { error: e });
  }
}

function flushLogsBeacon(): void {
  try {
    if (logBuffer.length === 0 || !logEndpointUrl || !sessionId) {
      return;
    }

    const logs = logBuffer;
    logBuffer = [];

    const blob = new Blob([JSON.stringify({ sessionId, logs })], {
      type: "text/plain",
    });
    navigator.sendBeacon(logEndpointUrl, blob);
  } catch (e) {
    log("Failed to flush logs", { error: e });
  }
}

export const handleAppException = (e_: unknown) => {
  let e: Error;

  if (e_ instanceof Error) {
    e = e_;
  } else {
    e = new Error(String(e_));
  }

  const errorContext: ErrorContext = {
    error: { message: e.message, stack: e.stack, name: e.name },
  };

  log(errorContext.error.message, errorContext);
  datadogLog("error", errorContext.error.message, errorContext);
};

export const executeAndMonitor = async <T>(
  fn: () => Promise<T>,
): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (e) {
    handleAppException(e);
    return undefined;
  }
};
